import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import SessionActivityMonitor from "./components/SessionActivityMonitor";
import { Layout, Menu, Button, Drawer, Grid, Tooltip, Modal, Form, Input, Alert, Result, message } from "antd";
import {
  DashboardOutlined,
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  PieChartOutlined,
  FileTextOutlined,
  LockOutlined,
  SafetyOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  clearClientSession,
  getClientToken,
  handleClientAuthFailure,
} from "./config/auth";
import {
  disableBiometricLogin,
  enableBiometricLogin,
  getBiometricCapability,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";
import { initializeMyWealthPush, unregisterMyWealthPushFromServer } from "./config/pushNotifications";

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

function AppLayoutForClient() {
  const authToken = getClientToken();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isDesktop = screens.lg;
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const clientName = localStorage.getItem("client_name") || "Client";
  const [dashboardPan, setDashboardPan] = useState("");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNativeBiometricRealm("client")) return;
      const capability = await getBiometricCapability("client");
      const enabled = capability.available ? await hasBiometricLogin("client") : false;
      if (alive) {
        setBiometricAvailable(capability.available);
        setBiometricEnabled(enabled);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!authToken) return;
    initializeMyWealthPush().catch(() => {});
  }, [authToken]);

  useEffect(() => {
    // Hooks must always run in the same order. If there is no token,
    // the protected layout redirects below and no API request is made.
    if (!authToken) return;

    const token = authToken;

    fetch(`${API_BASE}/client_me.php`, {
      method: "GET",
      headers: {
        "X-KG-Client-Authorization": `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (handleClientAuthFailure(res, data)) return null;
        if (!res.ok) throw new Error(data?.message || "Unable to verify client session.");
        return data;
      })
      .then((data) => {
        if (!data) return;
        if (data?.status === "ok" && data?.client?.dashboard_pan) {
          setDashboardPan(data.client.dashboard_pan);
        }
      })
      .catch(() => {
        // Testing display only. Never fall back to a browser-supplied PAN.
        setDashboardPan("");
      });
  }, [authToken]);

  // Do not render protected client content without a session token.
  // Keep this redirect after all Hooks so React Hook ordering stays valid.
  if (!authToken) return <Navigate to="/client/login" replace />;

  const openChangePassword = () => {
    setDrawerOpen(false);
    setChangePasswordError("");
    setChangePasswordSuccess(false);
    changePasswordForm.resetFields();
    setChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    if (changePasswordLoading) return;
    setChangePasswordOpen(false);
    setChangePasswordError("");
    setChangePasswordSuccess(false);
    changePasswordForm.resetFields();
  };

  const submitChangePassword = async (values) => {
    setChangePasswordLoading(true);
    setChangePasswordError("");

    try {
      const token = getClientToken();

      if (!token) {
        clearClientSession();
        window.location.replace("/client/login");
        return;
      }

      const response = await fetch(`${API_BASE}/client_change_password.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Client-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: values.current_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (handleClientAuthFailure(response, data)) {
        return;
      }

      if (!response.ok || data.status !== "ok") {
        setChangePasswordError(
          data.message || "Unable to change password at this time."
        );
        return;
      }

      setChangePasswordError("");
      changePasswordForm.resetFields();
      setChangePasswordSuccess(true);
    } catch (error) {
      setChangePasswordError(
        error.message || "Unable to connect to server."
      );
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const toggleBiometric = async () => {
    if (biometricBusy || !biometricAvailable) return;
    setDrawerOpen(false);
    setBiometricBusy(true);
    try {
      if (biometricEnabled) {
        // Reading the stored device credential requires biometric authentication.
        // Revoke it on the server first; only then remove the local secure copy.
        const saved = await readBiometricLogin("client");
        if (!saved?.deviceCredential) {
          await disableBiometricLogin("client");
          setBiometricEnabled(false);
          message.success("Biometric login disabled on this device.");
          return;
        }

        await fetch(`${API_BASE}/client_biometric_revoke.php`, {
          method: "POST",
          headers: {
            "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
          },
        }).then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data?.status !== "ok") {
            throw new Error(data?.message || "Unable to revoke biometric credential.");
          }
        });

        await disableBiometricLogin("client");
        setBiometricEnabled(false);
        message.success("Biometric login disabled on this device.");
      } else {
        const token = getClientToken();
        if (!token) throw new Error("Session expired.");

        const enrollRes = await fetch(`${API_BASE}/client_biometric_enroll.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-KG-Client-Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ device_label: "MFSetu - MyWealth" }),
        });
        const enrollData = await enrollRes.json().catch(() => ({}));
        const deviceCredential = enrollData?.device_credential;
        if (!enrollRes.ok || enrollData?.status !== "ok" || !deviceCredential) {
          throw new Error(enrollData?.message || "Biometric enrollment failed.");
        }

        await enableBiometricLogin("client", {
          deviceCredential,
          userId: localStorage.getItem("client_user_id") || "",
          displayName: localStorage.getItem("client_name") || "",
        });
        setBiometricEnabled(true);
        message.success("Biometric login enabled on this device.");
      }
    } catch (error) {
      if (!isBiometricCancellation(error)) message.error("Unable to change biometric login setting.");
    } finally {
      setBiometricBusy(false);
    }
  };

  const openNotifications = async () => {
    setDrawerOpen(false);
    setNotificationsOpen(true);
    setNotificationsLoading(true);
    try {
      const token = getClientToken();
      const response = await fetch(`${API_BASE}/client_notifications.php`, {
        headers: { "X-KG-Client-Authorization": `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (handleClientAuthFailure(response, data)) return;
      if (!response.ok || data.status !== "ok") throw new Error();
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (_) {
      message.error("Unable to load notifications.");
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markNotificationRead = async (item) => {
    const token = getClientToken();
    if (!token) return;
    try {
      await fetch(`${API_BASE}/client_notification_read.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-KG-Client-Authorization": `Bearer ${token}` },
        body: JSON.stringify({ broadcast_id: item.id }),
      });
      setNotifications((items) => items.map((n) => n.id === item.id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
      if (item.deep_link && String(item.deep_link).startsWith("/client/")) navigate(item.deep_link);
    } catch (_) {}
  };

  const logout = async () => {
    const token = getClientToken();

    try {
      if (token) {
        await unregisterMyWealthPushFromServer();
        await fetch(`${API_BASE}/client_logout.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-KG-Client-Authorization": `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // Local logout must still complete if the network is unavailable.
    } finally {
      clearClientSession();
      window.location.replace("/client/login");
    }
  };

  const menuItems = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      onClick: () => navigate("/client/dashboard"),
    },
    {
      key: "notifications",
      icon: <BellOutlined />,
      label: "Notifications",
      onClick: openNotifications,
    },
    {
      key: "change-password",
      icon: <LockOutlined />,
      label: "Change Password",
      onClick: openChangePassword,
    },
    ...(biometricAvailable ? [{
      key: "biometric-login",
      icon: <SafetyOutlined />,
      label: biometricBusy
        ? "Biometric Login..."
        : `Biometric Login: ${biometricEnabled ? "ON" : "OFF"}`,
      onClick: toggleBiometric,
    }] : []),
    {
      key: "portfolio",
      icon: <PieChartOutlined />,
      label: "Portfolio (Coming Soon)",
      disabled: true,
    },
    {
      key: "reports",
      icon: <FileTextOutlined />,
      label: "Reports (Coming Soon)",
      disabled: true,
    },
  ];

  const SidebarHeader = (
    <div style={{ padding: "10px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
      {isDesktop && (
        <Tooltip title={collapsed ? "Expand" : "Collapse"}>
          <Button type="text" size="small" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed((v) => !v)} />
        </Tooltip>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flex: 1, overflow: "hidden" }}>
        <UserOutlined style={{ marginTop: 3 }} />
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25 }}>
              {clientName}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 3, whiteSpace: "nowrap" }}>
              PAN: {dashboardPan || "Verifying..."}
            </div>
          </div>
        )}
      </div>
      {!collapsed && (
        <Tooltip title="Logout">
          <Button type="text" danger icon={<LogoutOutlined />} onClick={logout} />
        </Tooltip>
      )}
    </div>
  );

  const SidebarMenu = <Menu mode="inline" selectable={false} items={menuItems} />;

  return (
    <>
      <SessionActivityMonitor realm="client" />
      <Layout style={{ minHeight: "100vh" }}>
      {!isDesktop && (
        <Button type="primary" shape="circle" size="large" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)}
          style={{ position: "fixed", top: 12, left: 12, zIndex: 1000 }} aria-label="Open client menu" />
      )}

      {isDesktop && (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={240} theme="light"
          style={{ borderRight: "1px solid #f0f0f0", background: "#fff" }}>
          {SidebarHeader}
          {SidebarMenu}
        </Sider>
      )}

      {!isDesktop && (
        <Drawer title={<span><UserOutlined /> {clientName}{dashboardPan ? <span style={{ display: "block", fontSize: 11, fontWeight: 400, color: "#8c8c8c", marginLeft: 22 }}>Dashboard PAN: {dashboardPan}</span> : null}</span>} placement="left" onClose={() => setDrawerOpen(false)} open={drawerOpen}
          bodyStyle={{ padding: 0 }} extra={<Button type="text" danger icon={<LogoutOutlined />} onClick={logout} />}>
          {SidebarMenu}
        </Drawer>
      )}

      <Layout>
        <Content style={{ background: "#f5f7fa", minHeight: "100vh", padding: isDesktop ? 28 : "72px 16px 20px" }}>
          <Outlet />
        </Content>
      </Layout>

      <Modal
        open={notificationsOpen}
        centered
        width={620}
        title="Notifications"
        footer={null}
        onCancel={() => setNotificationsOpen(false)}
      >
        {notificationsLoading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <Alert type="info" showIcon message="No notifications yet." />
        ) : (
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {notifications.map((item) => (
              <div key={item.id} onClick={() => markNotificationRead(item)} style={{ padding: "12px 4px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", opacity: item.read_at ? 0.7 : 1 }}>
                <div style={{ fontWeight: item.read_at ? 500 : 700 }}>{item.title}</div>
                <div style={{ marginTop: 4 }}>{item.message}</div>
                <div style={{ marginTop: 5, fontSize: 11, color: "#8c8c8c" }}>{item.sent_at || ""}</div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={changePasswordOpen}
        centered
        title="Change Password"
        okText="Change Password"
        cancelText="Cancel"
        confirmLoading={changePasswordLoading}
        closable={!changePasswordLoading}
        maskClosable={false}
        keyboard={!changePasswordLoading}
        onCancel={closeChangePassword}
        onOk={() => changePasswordForm.submit()}
        footer={changePasswordSuccess ? null : undefined}
        destroyOnHidden
      >
        {changePasswordSuccess ? (
          <Result
            status="success"
            title="Password Changed Successfully"
            subTitle="Your password has been changed successfully."
            extra={[
              <Button
                type="primary"
                key="ok"
                size="large"
                onClick={closeChangePassword}
              >
                OK
              </Button>,
            ]}
          />
        ) : (
          <>
        {changePasswordError && (
          <Alert
            type="error"
            showIcon
            message={changePasswordError}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={changePasswordForm}
          layout="vertical"
          autoComplete="off"
          onFinish={submitChangePassword}
        >
          <Form.Item
            label="Current Password"
            name="current_password"
            rules={[
              {
                required: true,
                message: "Please enter your Current Password.",
              },
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Enter Current Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="new_password"
            dependencies={["current_password"]}
            rules={[
              {
                required: true,
                message: "Please enter your New Password.",
              },
              {
                min: 8,
                message: "Password must be at least 8 characters.",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!/[A-Z]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one uppercase letter.")
                    );
                  }
                  if (!/[a-z]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one lowercase letter.")
                    );
                  }
                  if (!/[0-9]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one number.")
                    );
                  }
                  if (!/[^A-Za-z0-9]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one special character.")
                    );
                  }
                  if (
                    value === changePasswordForm.getFieldValue("current_password")
                  ) {
                    return Promise.reject(
                      new Error(
                        "New Password must be different from Current Password."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Enter New Password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirm_password"
            dependencies={["new_password"]}
            rules={[
              {
                required: true,
                message: "Please confirm your New Password.",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      "New Password and Confirm Password do not match."
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Confirm New Password"
              autoComplete="new-password"
              onPressEnter={() => changePasswordForm.submit()}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            message="Password must have at least 8 characters with uppercase, lowercase, number and special character."
          />
        </Form>
          </>
        )}
      </Modal>
      </Layout>
    </>
  );
}

export default AppLayoutForClient;
